import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { localDate, days, money } from './services/api';
import { I18nProvider, useI18n, translateError } from './services/i18n';
import './styles/main.css';

import TabBar from './components/common/TabBar';
import Onboarding from './components/onboarding/Onboarding';
import OrderForm from './components/order/OrderForm';
import OrderConfirm from './components/order/OrderConfirm';
import OrderList from './components/orders/OrderList';
import Wallet from './components/wallet/Wallet';
import Referral from './components/referral/Referral';

function MainApp() {
  const { t, setLang } = useI18n();
  const [screen, setScreen] = useState('order');
  const [me, setMe] = useState(null);
  const [dayIdx, setDayIdx] = useState(0);
  const [slots, setSlots] = useState(null);
  const [start, setStart] = useState(null);
  const [dur, setDur] = useState(3);
  const [model, setModel] = useState('');
  const [lastOrder, setLastOrder] = useState(null);
  const [ordersTab, setOrdersTab] = useState('upcoming');
  const [orders, setOrders] = useState(null);
  const [topup, setTopup] = useState(50);
  const [transactions, setTransactions] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  const isInitialMount = useRef(true);

  const showToast = useCallback((text) => {
    setToastMsg(text);
    setTimeout(() => {
      setToastMsg((cur) => (cur === text ? '' : cur));
    }, 2200);
  }, []);

  // Telegram WebApp initialization
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready?.();
      tg.expand?.();
      tg.setHeaderColor?.('#141414');
      tg.setBackgroundColor?.('#141414');
    }
  }, []);

  // Load User Data on Mount
  const loadMe = useCallback(async () => {
    try {
      const data = await api.me();
      setMe(data);
      if (data?.language_code || data?.language) {
        const langCode = (data.language_code || data.language).toLowerCase();
        if (langCode.startsWith('en')) {
          setLang('en');
        } else if (langCode.startsWith('ru')) {
          setLang('ru');
        }
      }
      if (data?.recent_models && data.recent_models.length > 0) {
        setModel((cur) => cur || data.recent_models[0]);
      }
      return data;
    } catch (err) {
      console.error('Failed to load user info', err);
      return null;
    }
  }, [setLang]);

  useEffect(() => {
    (async () => {
      const data = await loadMe();
      const hasOnboarded = localStorage.getItem('onboarded') === '1' || (data && data.onboarded);
      if (!hasOnboarded) {
        setScreen('onboarding');
      }
      isInitialMount.current = false;
    })();
  }, [loadMe]);

  // Telegram BackButton integration
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg?.BackButton) return;

    if (screen === 'confirm') {
      tg.BackButton.show();
      const handleBack = () => setScreen('order');
      tg.BackButton.onClick(handleBack);
      return () => {
        tg.BackButton.offClick?.(handleBack);
        tg.BackButton.hide();
      };
    } else {
      tg.BackButton.hide();
    }
  }, [screen]);

  // Data loaders
  const loadSlots = useCallback(async (date) => {
    setSlots(null);
    try {
      const res = await api.slots(date);
      setSlots(res.hours);
    } catch (err) {
      console.error('Failed to load slots', err);
      setSlots([]);
    }
  }, []);

  const loadOrders = useCallback(async (scope) => {
    setOrders(null);
    try {
      const res = await api.orders(scope);
      setOrders(res.orders || []);
    } catch (err) {
      console.error('Failed to load orders', err);
      setOrders([]);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    setTransactions(null);
    try {
      const res = await api.transactions();
      setTransactions(res.transactions || []);
    } catch (err) {
      console.error('Failed to load transactions', err);
      setTransactions([]);
    }
  }, []);

  const loadReferralData = useCallback(async () => {
    setReferralData(null);
    try {
      const res = await api.referral();
      setReferralData(res);
    } catch (err) {
      console.error('Failed to load referral data', err);
      setReferralData(null);
    }
  }, []);

  // Screen navigation
  const navigateTo = (targetScreen) => {
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
    if (window.scrollTo) window.scrollTo(0, 0);
    setScreen(targetScreen);
  };

  // Actions
  const handleCreateOrder = async ({ model: orderModel, start: startIso, hours: orderHours }) => {
    setBusy(true);
    try {
      const res = await api.createOrder({
        model: orderModel,
        start: startIso,
        hours: orderHours,
        remind: true
      });

      setLastOrder(res.order);
      setMe((prev) => ({
        ...prev,
        balance: res.balance,
        recent_models: [orderModel, ...(prev?.recent_models || []).filter((m) => m !== orderModel)].slice(0, 5)
      }));
      setSlots(null);
      setOrders(null);
      setTransactions(null);

      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('success');
      setScreen('confirm');
    } catch (err) {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('error');
      if (err.status === 409 || err.code === 'task_time_busy') {
        showToast(t('toast_slot_taken'));
        loadSlots(localDate(days()[dayIdx]));
      } else if (err.status === 402 || err.code === 'insufficient_funds') {
        await loadMe();
        showToast(t('toast_insufficient'));
      } else {
        showToast(translateError(err, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    const tg = window.Telegram?.WebApp;
    const confirmPrompt = t('confirm_cancel_prompt');

    const proceed = await new Promise((resolve) => {
      if (tg?.showConfirm) {
        tg.showConfirm(confirmPrompt, resolve);
      } else {
        resolve(window.confirm(confirmPrompt));
      }
    });

    if (!proceed) return;

    try {
      const res = await api.cancelOrder(orderId);
      setMe((prev) => ({ ...prev, balance: res.balance }));
      setSlots(null);
      tg?.HapticFeedback?.notificationOccurred?.('success');
      showToast(t('toast_cancelled_refund', { amount: money(res.refunded) }));
      loadOrders(ordersTab);
    } catch (err) {
      tg?.HapticFeedback?.notificationOccurred?.('error');
      showToast(translateError(err, t));
    }
  };

  const handleRepeatOrder = (order) => {
    setModel(order.model);
    setDur(order.hours || 3);
    setStart(null);
    setDayIdx(0);
    setSlots(null);
    navigateTo('order');
  };

  const handleToggleReminder = async () => {
    if (!lastOrder) return;
    const nextRemind = !lastOrder.remind;
    setLastOrder((prev) => ({ ...prev, remind: nextRemind }));
    try {
      await api.setReminder(lastOrder.id, nextRemind);
    } catch (e) {
      console.error('Failed to set reminder', e);
    }
  };

  const handleTopup = async (amount) => {
    setBusy(true);
    try {
      const res = await api.topup(amount);
      const url = res.pay_url;
      if (window.Telegram?.WebApp?.openLink) {
        window.Telegram.WebApp.openLink(url);
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      showToast(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };

  const showTabBar = screen !== 'onboarding' && screen !== 'confirm';

  return (
    <div id="app">
      {screen === 'onboarding' && (
        <Onboarding onFinish={(target) => navigateTo(target || 'order')} />
      )}

      {screen === 'order' && (
        <OrderForm
          me={me}
          slots={slots}
          loadSlots={loadSlots}
          model={model}
          setModel={setModel}
          dayIdx={dayIdx}
          setDayIdx={setDayIdx}
          start={start}
          setStart={setStart}
          dur={dur}
          setDur={setDur}
          onNavigate={navigateTo}
          onCreateOrder={handleCreateOrder}
          busy={busy}
          onOpenOnboarding={() => navigateTo('onboarding')}
        />
      )}

      {screen === 'confirm' && (
        <OrderConfirm
          order={lastOrder}
          balance={me?.balance || 0}
          onNavigate={navigateTo}
          onToggleReminder={handleToggleReminder}
        />
      )}

      {screen === 'orders' && (
        <OrderList
          orders={orders}
          ordersTab={ordersTab}
          setOrdersTab={setOrdersTab}
          loadOrders={loadOrders}
          onCancelOrder={handleCancelOrder}
          onRepeatOrder={handleRepeatOrder}
          onNavigate={navigateTo}
        />
      )}

      {screen === 'wallet' && (
        <Wallet
          me={me}
          topup={topup}
          setTopup={setTopup}
          transactions={transactions}
          loadTransactions={loadTransactions}
          onTopup={handleTopup}
          busy={busy}
        />
      )}

      {screen === 'referral' && (
        <Referral
          referralData={referralData}
          loadReferralData={loadReferralData}
          onShowToast={showToast}
        />
      )}

      {showTabBar && (
        <TabBar activeTab={screen} onTabChange={navigateTo} />
      )}

      {toastMsg && (
        <div className="toast" data-testid="app-toast">
          {toastMsg}
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <MainApp />
    </I18nProvider>
  );
}
